// ./src/components/generic/PullToRefresh/PullToRefresh.tsx
// Generic pull-to-refresh component for mobile/tablet devices
// Uses react-simple-pull-to-refresh library
// Documentation: https://www.npmjs.com/package/react-simple-pull-to-refresh

import React, { useCallback } from "react";
import { Box, Text, Loader, useMantineTheme } from "@mantine/core";
import { useTranslation } from "react-i18next";
import PullToRefreshLib from "react-simple-pull-to-refresh";

import { AppIcon } from "../AppIcon";

import classes from "./PullToRefresh.module.css";

import { useAppEnvironment } from "@/contexts/AppEnvironmentContext";

// ============================================
// Types
// ============================================

export interface PullToRefreshProps {
  /** Callback function that returns a Promise - called when user pulls to refresh */
  onRefresh: () => Promise<void>;
  /** Content to wrap with pull-to-refresh functionality */
  children: React.ReactNode;
  /** Custom pulling content (optional) */
  pullingContent?: React.ReactNode;
  /** Custom refreshing content (optional) */
  refreshingContent?: React.ReactNode;
  /** Distance in pixels to trigger refresh (default: 100) */
  pullDownThreshold?: number;
  /** Maximum pull down distance in pixels (default: 150) */
  maxPullDownDistance?: number;
  /** Resistance factor for pull gesture (default: 4) */
  resistance?: number;
  /** Force disable pull-to-refresh regardless of screen size */
  disabled?: boolean;
  /** Background color for the pull indicator area */
  backgroundColor?: string;
  /** Custom breakpoint for mobile detection (default: "sm") */
  mobileBreakpoint?: "xs" | "sm" | "md" | "lg" | "xl";
}

// ============================================
// Pulling Content Component
// ============================================

interface PullingContentProps {
  backgroundColor?: string;
}

const PullingContent: React.FC<PullingContentProps> = ({ backgroundColor }) => {
  const { t } = useTranslation("common");
  const theme = useMantineTheme();

  return (
    <Box
      style={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: theme.spacing.sm,
        padding: theme.spacing.sm,
        height: 67,
        backgroundColor: backgroundColor || theme.colors.neutral[0],
      }}
    >
      <AppIcon iconName="Refresh" size={24} color={theme.colors.neutral[5]} />
      <Text size="sm" c="dimmed">
        {t("pullToRefresh.pulling")}
      </Text>
    </Box>
  );
};

// ============================================
// Refreshing Content Component
// ============================================

interface RefreshingContentProps {
  backgroundColor?: string;
}

const RefreshingContent: React.FC<RefreshingContentProps> = ({
  backgroundColor,
}) => {
  const { t } = useTranslation("common");
  const theme = useMantineTheme();

  return (
    <Box
      style={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: theme.spacing.sm,
        padding: theme.spacing.sm,
        height: 67,
        backgroundColor: backgroundColor || theme.colors.neutral[0],
      }}
    >
      <Loader size={24} color="brand" />
      <Text size="sm" c="dimmed">
        {t("pullToRefresh.refreshing")}
      </Text>
    </Box>
  );
};

// ============================================
// Main Component
// ============================================

export const PullToRefresh: React.FC<PullToRefreshProps> = ({
  onRefresh,
  children,
  pullingContent,
  refreshingContent,
  pullDownThreshold = 100,
  maxPullDownDistance = 150,
  resistance = 3,
  disabled = false,
  backgroundColor,
}) => {
  // Detect if we're on mobile/tablet
  const { isMobile } = useAppEnvironment();

  // Determine if pull-to-refresh should be enabled
  const isPullable = !disabled && isMobile;

  // Wrap the refresh handler with error handling
  // This ensures the pull-to-refresh component works properly even if the handler throws
  // ⓘ An async function always resolves here (the catch swallows), which is what resets the component.
  const handleRefreshWithErrorHandling = useCallback(async (): Promise<void> => {
    try {
      if (typeof onRefresh === "function") {
        await onRefresh();
      } else {
        console.warn(
          "[PullToRefresh] The 'onRefresh' prop must be a function that returns a promise"
        );
      }
    } catch (error) {
      // Log the error but don't throw - this ensures the pull-to-refresh
      // component returns to its normal state
      console.error("[PullToRefresh] Error during refresh:", error);
    }
  }, [onRefresh]);

  // Default pulling content
  const defaultPullingContent = (
    <PullingContent backgroundColor={backgroundColor} />
  );

  // Default refreshing content
  const defaultRefreshingContent = (
    <RefreshingContent backgroundColor={backgroundColor} />
  );

  return (
    <div className={classes.pullToRefreshWrapper}>
      <PullToRefreshLib
        isPullable={isPullable}
        onRefresh={handleRefreshWithErrorHandling}
        pullingContent={pullingContent || defaultPullingContent}
        refreshingContent={refreshingContent || defaultRefreshingContent}
        pullDownThreshold={pullDownThreshold}
        maxPullDownDistance={maxPullDownDistance}
        resistance={resistance}
        canFetchMore={false}
      >
        {children}
      </PullToRefreshLib>
    </div>
  );
};

// ============================================
// Exports
// ============================================

export { PullingContent, RefreshingContent };